import { SubscriberPackageKey } from 'library/ts/model/user/SubscriberPackageKey';
import { AbstractFreewheelClass } from 'tag/ts/tag/ads/freewheel/abstract/AbstractFreewheelClass';
import { IFWCustomDAIAPIParams } from 'tag/ts/tag/interfaces/ads/freewheel/IFWCustomDAIAPIParams';
import { IFWCustomDAIParams } from 'tag/ts/tag/interfaces/ads/freewheel/IFWCustomDAIParams';
import { IFWCustomIMAParams } from 'tag/ts/tag/interfaces/ads/freewheel/IFWCustomIMAParams';
import { IFWCustomLinearParams } from 'tag/ts/tag/interfaces/ads/freewheel/IFWCustomLinearParams';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { FreewheelConstants } from 'tag/ts/tag/model/FreewheelConstants';
import { UserModel } from 'tag/ts/tag/model/vo/UserModel';
import { TFWCustomParams } from 'tag/ts/tag/types/ads/freewheel/TFWCustomParams';

/**
 * @copyright © Paramount Streaming
 * @description Abstract class for Freewheel custom parameters
 */
export abstract class AbstractFWCustomParams extends AbstractFreewheelClass {

	/**
	 * @description Internal store for custom ad parameters
	 * @type {object}
	 * @readonly
	 */
	protected readonly abstract adParams: object;

	protected slotParams: TFWCustomParams = {};

	protected companionSlotParams: TFWCustomParams = {};

	#utils = window?.SmartTag?.utilities;

	protected constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				name: 'AbstractFWCustomParams',
				...argument
			}
		);
	}

	/**
	 * @description Returns custom parameters
	 * @returns {object}
	 */
	get adParameters(): object {
		return this.adParams;
	}

	/**
	 * @description Returns slot parameters
	 * @returns {TFWCustomParams}
	 */
	get adSlotParameters(): TFWCustomParams {
		return this.slotParams;
	}

	/**
	 * @description Returns companion slot parameters
	 * @returns {TFWCustomParams}
	 */
	get adCompanionSlotParameters(): TFWCustomParams {
		return this.companionSlotParams;
	}

	protected createLinearParams(): IFWCustomLinearParams {
		const paramsVO = this?.tag?.params;
		const siteSessionData = this?.tag?.model?.siteUtilities?.siteSessionData;
		const constants = FreewheelConstants.getInstance();
		const tfcd = !this.#utils.Type.isEmpty( siteSessionData?.tfcd ) ? siteSessionData.tfcd.toString() : undefined;
		const userModel = new UserModel( { tag: this?.tag, paramsUser: paramsVO?.USER } );
		const currentValidStatus = userModel.getCurrentValidStatus();

		const params: IFWCustomLinearParams = {
			ge: siteSessionData?.ge,
			gr: siteSessionData?.gr,
			packageSource: paramsVO?.USER?.packageSource,
			sub: siteSessionData?.sub,
			subses: siteSessionData?.subses,
			sz: constants.size,
			sb: currentValidStatus?.key === SubscriberPackageKey.SB ? currentValidStatus?.value : undefined,
			tfcd: tfcd,
			tve: currentValidStatus?.key === SubscriberPackageKey.TVE ? currentValidStatus?.value : undefined,
			tsb: currentValidStatus?.key === SubscriberPackageKey.TSB ? currentValidStatus?.value : undefined,
			user: 'ADULT',
			session: siteSessionData?.session,
			vguid: siteSessionData?.vguid,
			station: this?.tag?.model?.adUtilities?.getCallSignId?.()
		};
		this?.logger?.log( 'createLinearParams:', params );
		return params;
	}

	protected createLinearSlotParams(): IFWCustomLinearParams {
		const constants = FreewheelConstants.getInstance();

		const params: IFWCustomLinearParams = {
			cpsq: '1',
			maxd: constants.durTag,
			mind: constants.durTag,
			ptgt: 'a',
			slid: '',
			tpcl: constants.midroll,
			slau: constants.slau,
			tpos: '0'
		};
		this?.logger?.log( 'createLinearSlotParams:', params );
		return params;
	}

	protected createLinearCompanionSlotParams(): IFWCustomLinearParams {
		const params: IFWCustomLinearParams = {
			slid: 'companion',
			slau: 'companion',
			w: '1920',
			h: '375',
			flag: '+cmpn',
			prct: 'application/json,text/html_doc_ref',
			ptgt: 'p'
		};
		this?.logger?.log( 'createLinearCompanionSlotParams:', params );
		return params;
	}

	protected createIMAParams(): IFWCustomIMAParams {
		const paramsVO = this?.tag?.params;
		const siteSessionData = this?.tag?.model?.siteUtilities?.siteSessionData;
		const constants = FreewheelConstants.getInstance();
		const isLive = !!this?.tag?.model?.contentUtilities?.isLive( paramsVO, this.apiMetadata );
		const tfcd = !this.#utils.Type.isEmpty( siteSessionData?.tfcd ) ? siteSessionData.tfcd.toString() : undefined;
		const userModel = new UserModel( { tag: this?.tag, paramsUser: paramsVO?.USER } );
		const currentValidStatus = userModel.getCurrentValidStatus();

		const params: IFWCustomIMAParams = {
			cpPre: !!isLive ? undefined : siteSessionData?.cpPre,
			cpSession: !!isLive ? undefined : siteSessionData?.cpSession,
			ge: siteSessionData?.ge,
			gr: siteSessionData?.gr,
			sub: siteSessionData?.sub,
			subses: siteSessionData?.subses,
			sz: constants.size,
			tfcd: tfcd,
			tve: currentValidStatus?.key === SubscriberPackageKey.TVE ? currentValidStatus?.value : undefined,
			tsb: currentValidStatus?.key === SubscriberPackageKey.TSB ? currentValidStatus?.value : undefined,
			user: paramsVO?.CONTEXT?.metadata?.userProfileCategory,
			vguid: siteSessionData?.vguid,
			host: siteSessionData?.host,
			partner: siteSessionData?.partner,
			ptype: siteSessionData?.ptype,
			session: siteSessionData?.session,
			sb: currentValidStatus?.key === SubscriberPackageKey.SB ? currentValidStatus?.value : undefined,
			section: siteSessionData?.section,
			bundle: userModel.bundleStatuses,
			packageSource: paramsVO?.USER?.packageSource,
			_fw_h_x_country: siteSessionData?.region,
			_fw_content_language: siteSessionData?.lang,
			device_region_language: siteSessionData?.device_region_language,
			language_selection: this.createLanguageSelection(),
			endcard: siteSessionData?.endcard,
			gpp: siteSessionData?.gpp,
			gpp_sid: siteSessionData?.gpp_sid,
			cadence: siteSessionData?.billing,
			sublife: siteSessionData?.sublife,
			packageType: siteSessionData?.packageType,
			profile: siteSessionData?.profile?.toString(),
			vendorUserType: siteSessionData?.vendorUserType
		};
		this?.logger?.log( 'createIMAParams:', params );
		return params;
	}

	protected createDAIParams(): IFWCustomDAIParams {
		const paramsVO = this?.tag?.params;
		const siteSessionData = this?.tag?.model?.siteUtilities?.siteSessionData;
		const constants = FreewheelConstants.getInstance();
		const isLive = !!this?.tag?.model?.contentUtilities?.isLive( paramsVO, this.apiMetadata );
		const tfcd = !this.#utils.Type.isEmpty( siteSessionData?.tfcd ) ? siteSessionData.tfcd.toString() : undefined;
		const userModel = new UserModel( { tag: this?.tag, paramsUser: paramsVO?.USER } );
		const currentValidStatus = userModel.getCurrentValidStatus();

		const params: IFWCustomDAIParams = {
			imafw_ge: siteSessionData?.ge,
			imafw_gr: siteSessionData?.gr,
			imafw_sub: siteSessionData?.sub,
			imafw_subses: siteSessionData?.subses,
			imafw_sz: constants.size,
			tfcd: tfcd,
			imafw_tfcd: tfcd,
			imafw_tve: currentValidStatus?.key === SubscriberPackageKey.TVE ? currentValidStatus?.value : undefined,
			imafw_tsb: currentValidStatus?.key === SubscriberPackageKey.TSB ? currentValidStatus?.value : undefined,
			imafw_user: paramsVO?.CONTEXT?.metadata?.userProfileCategory,
			imafw_vguid: siteSessionData?.vguid,
			imafw_host: siteSessionData?.host,
			imafw_partner: siteSessionData?.partner,
			imafw_ptype: siteSessionData?.ptype,
			imafw_session: siteSessionData?.session,
			imafw_sb: currentValidStatus?.key === SubscriberPackageKey.SB ? currentValidStatus?.value : undefined,
			imafw_section: siteSessionData?.section,
			imafw_bundle: userModel.bundleStatuses,
			imafw_packageSource: paramsVO?.USER?.packageSource,
			imafw_source: siteSessionData?.source,
			imafw_showSection: siteSessionData?.showSection,
			imafw_movieSection: siteSessionData?.movieSection,
			imafw_searchEventComplete: siteSessionData?.searchEventComplete,
			imafw__fw_h_x_country: siteSessionData?.region,
			imafw_device_region_language: siteSessionData?.device_region_language,
			imafw_language_selection: this.createLanguageSelection(),
			imafw__fw_content_language: siteSessionData?.lang,
			imafw_endcard: siteSessionData?.endcard,
			imafw_gpp: siteSessionData?.gpp,
			imafw_gpp_sid: siteSessionData?.gpp_sid,
			imafw_cadence: siteSessionData?.billing,
			imafw_sublife: siteSessionData?.sublife,
			imafw_packageType: siteSessionData?.packageType,
			imafw_profile: siteSessionData?.profile?.toString(),
			imafw_vendorUserType: siteSessionData?.vendorUserType
		};
		if ( !isLive ) {
			params.imafw_cpPre = siteSessionData?.cpPre;
			params.imafw_cpSession = siteSessionData?.cpSession;
		}
		this?.logger?.log( 'createDAIParams:', params );
		return params;
	}

	protected createDAIAPIParams(): IFWCustomDAIAPIParams {
		const paramsVO = this?.tag?.params;
		const siteSessionData = this?.tag?.model?.siteUtilities?.siteSessionData;
		const constants = FreewheelConstants.getInstance();
		const tfcd = !this.#utils.Type.isEmpty( siteSessionData?.tfcd ) ? siteSessionData.tfcd.toString() : undefined;
		const userModel = new UserModel( { tag: this?.tag, paramsUser: paramsVO?.USER } );
		const currentValidStatus = userModel.getCurrentValidStatus();

		const params: IFWCustomDAIAPIParams = {
			imafw_ge: siteSessionData?.ge,
			imafw_gr: siteSessionData?.gr,
			imafw_sub: siteSessionData?.sub,
			imafw_subses: siteSessionData?.subses,
			imafw_sz: constants.size,
			imafw_tfcd: tfcd,
			imafw_tsb: currentValidStatus?.key === SubscriberPackageKey.TSB ? currentValidStatus?.value : undefined,
			imafw_user: paramsVO?.CONTEXT?.metadata?.userProfileCategory,
			imafw_vguid: siteSessionData?.vguid,
			imafw_host: siteSessionData?.host,
			imafw_session: siteSessionData?.session,
			imafw_sb: currentValidStatus?.key === SubscriberPackageKey.SB ? currentValidStatus?.value : undefined,
			imafw_is_lat: '0',
			imafw_playername_version: 'Avia JS Video Player_1.9.1',
			imafw_bundle: userModel.bundleStatuses,
			imafw_packageSource: paramsVO?.USER?.packageSource,
			imafw_gpp: siteSessionData?.gpp,
			imafw_gpp_sid: siteSessionData?.gpp_sid,
			imafw_cadence: siteSessionData?.billing,
			imafw_sublife: siteSessionData?.sublife,
			imafw_packageType: siteSessionData?.packageType,
			imafw_profile: siteSessionData?.profile?.toString(),
			imafw_vendorUserType: siteSessionData?.vendorUserType
		};
		this?.logger?.log( 'createDAIAPIParams:', params );
		return params;
	}
}